import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import Button from "@civicplus/preamble-ui/lib/Button";
import Checkbox from "@civicplus/preamble-ui/lib/Checkbox";
import Dialog from "@civicplus/preamble-ui/lib/Dialog";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import TextInput from "@civicplus/preamble-ui/lib/TextInput";
import Typography from "@civicplus/preamble-ui/lib/Typography";
import { AddSubscriberRequest, SubscriptionType, User } from "../../entities/Subscriber";
import { bottle } from "../../provider/Bottle";
import { isNullOrWhiteSpace } from "@civicplus/preamble-ui/lib/Utilities/StringHelper";
import { PrivacyService } from "../../services/PrivacyService";
import { SimpleDialog } from "../../util/SimpleDialog";
import { SubscriberImportService } from "../../services/SubscriberImportService";
import { useSnackbar } from "notistack";
import enhanceWithValidation, {
    requiredValidation,
    emailValidation,
    ValidationResult
} from "@civicplus/preamble-ui/lib/Validations";

const EnhancedTextInput = enhanceWithValidation(TextInput);

interface AddSubscriberDialogProps {
    isOpen: boolean;
    selectedListId?: number;
    onClose: () => void;
    onSave: () => void;
}

export const AddSubscriberDialog: React.FC<AddSubscriberDialogProps> = (props) => {
    const { isOpen, selectedListId, onClose, onSave } = props;
    const privacyService: PrivacyService = bottle.container.PrivacyService;
    const importService: SubscriberImportService = bottle.container.SubscriberImportService;

    const { enqueueSnackbar } = useSnackbar();
    const [newSubscriber, setNewSubscriber] = useState<AddSubscriberRequest | null>(null);
    const [emailSubscription, setEmailSubscription] = useState<boolean>(false);
    const [smsSubscription, setSmsSubscription] = useState<boolean>(false);
    const [existingUser, setExistingUser] = useState<User | null>(null);
    const [doesUserExist, setDoesUserExist] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [showCustomDirtyDialog, setShowCustomDirtyDialog] = useState<boolean>(false);

    const emailRef = useRef<{ validate: () => ValidationResult }>();
    const firstNameRef = useRef<{ validate: () => ValidationResult }>();
    const lastNameRef = useRef<{ validate: () => ValidationResult }>();

    const verifyIfUserExists = useCallback(
        async (email: string) => {
            try {
                const validationResults: (ValidationResult | undefined)[] = await Promise.all([
                    emailRef.current?.validate()
                ]);

                if (validationResults.some((x) => x?.error)) {
                    setIsSaving(false);
                    enqueueSnackbar("Email must be valid.", { variant: "warning" });
                    return;
                }

                setDoesUserExist(false);
                setExistingUser(null);
                setIsLoading(true);

                const userInfo = await privacyService.getUserInfo(email);

                if (userInfo) {
                    setNewSubscriber({
                        ...newSubscriber,
                        firstName: userInfo.firstName,
                        lastName: userInfo.lastName,
                        phoneNumber: userInfo.phoneNumber
                    });
                }

                setExistingUser(userInfo);
                setDoesUserExist(userInfo ? true : false);
            } catch (error) {
                enqueueSnackbar(`The user '${newSubscriber?.email}' was not found.`, {
                    variant: "info"
                });

                setNewSubscriber({
                    ...newSubscriber,
                    firstName: "",
                    lastName: "",
                    phoneNumber: ""
                });
                setExistingUser(null);
                setDoesUserExist(false);
            } finally {
                setIsLoading(false);
            }
        },
        [newSubscriber, privacyService, enqueueSnackbar]
    );

    const onEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewSubscriber((prev) => ({ ...prev, email: e.target.value }));
        setIsDirty(true);
    }, []);

    const onFirstNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewSubscriber((prev) => ({ ...prev, firstName: e.target.value }));
        setIsDirty(true);
    }, []);

    const onLastNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewSubscriber((prev) => ({ ...prev, lastName: e.target.value }));
        setIsDirty(true);
    }, []);

    const onPhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setNewSubscriber((prev) => ({ ...prev, phoneNumber: e.target.value }));
        setIsDirty(true);
    }, []);

    const isFormValid = useMemo(() => {
        return (newSubscriber?.email &&
            newSubscriber.firstName &&
            newSubscriber.lastName &&
            (emailSubscription || smsSubscription)) as boolean;
    }, [newSubscriber, emailSubscription, smsSubscription]);

    const onSaveDialog = useCallback(async () => {
        setIsSaving(true);

        const validationResults: (ValidationResult | undefined)[] = await Promise.all([
            emailRef.current?.validate(),
            firstNameRef.current?.validate(),
            lastNameRef.current?.validate()
        ]);

        if (validationResults.some((x) => x?.error) || (emailSubscription === false && smsSubscription === false)) {
            setIsSaving(false);
            enqueueSnackbar("All required fields must be valid to continue.", { variant: "error", persist: true });
            return;
        }

        const subscriptionType =
            emailSubscription && smsSubscription
                ? SubscriptionType.EmailAndSms
                : emailSubscription
                  ? SubscriptionType.Email
                  : smsSubscription
                    ? SubscriptionType.Sms
                    : SubscriptionType.None;

        try {
            await importService.addSingleSubscriber(selectedListId!, {
                listId: selectedListId!,
                type: subscriptionType || SubscriptionType.Email,
                user: {
                    id: existingUser ? existingUser.id : "",
                    firstName: newSubscriber?.firstName || "",
                    lastName: newSubscriber?.lastName || "",
                    email: newSubscriber?.email || "",
                    phoneNumber: newSubscriber?.phoneNumber || "",
                    isValidated: existingUser ? existingUser.isValidated : false,
                    isEmailSuppressed: existingUser ? existingUser.isEmailSuppressed : false,
                    sendWelcomeEmail: true
                }
            });

            setIsDirty(false);

            setTimeout(() => {
                enqueueSnackbar("Subscriber added successfully.", { variant: "success" });
            }, 900);

            setTimeout(() => {
                onClose();

                if (onSave) {
                    onSave();
                }

                setIsSaving(false);
            }, 1100);
        } catch (error) {
            enqueueSnackbar("Failed to add subscriber. Please try again.", { variant: "warning" });
            setIsSaving(false);
            return;
        }
    }, [
        emailSubscription,
        smsSubscription,
        newSubscriber,
        enqueueSnackbar,
        importService,
        selectedListId,
        existingUser,
        onClose,
        onSave
    ]);

    const onCloseDialog = useCallback(() => {
        if (isDirty) {
            setShowCustomDirtyDialog(true);
            return;
        }

        onClose();
        setNewSubscriber(null);
    }, [isDirty, onClose]);

    const onExitedDialog = useCallback(() => {
        if (isDirty) {
            setShowCustomDirtyDialog(true);
            return;
        }

        setNewSubscriber(null);
    }, [isDirty]);

    useEffect(() => {
        if (isNullOrWhiteSpace(newSubscriber?.email)) {
            setNewSubscriber({
                email: "",
                firstName: "",
                lastName: "",
                phoneNumber: ""
            });
            setIsDirty(false);

            if (existingUser) {
                setExistingUser(null);
            }
        }
    }, [newSubscriber?.email, existingUser]);

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onCloseDialog}
                title="Add Subscriber"
                onExited={onExitedDialog}
                actions={[
                    <Button
                        color="primary"
                        onClick={onSaveDialog}
                        isLoading={isSaving}
                        id="add-subscriber-save-button"
                        key="add-subscriber-save-button"
                        data-testid="add-subscriber-save-button"
                        disabled={!isFormValid || isLoading}
                    >
                        Save
                    </Button>,
                    <Button onClick={onCloseDialog} key="cancel">
                        Cancel
                    </Button>
                ]}
            >
                <Grid container={true}>
                    <Grid xs={12}>
                        <EnhancedTextInput
                            id="email-input"
                            label="Email"
                            ref={emailRef}
                            value={newSubscriber?.email || ""}
                            onChange={onEmailChange}
                            required={true}
                            fullWidth={true}
                            disabled={isLoading}
                            validations={[requiredValidation, emailValidation]}
                            onBlur={() => {
                                if (newSubscriber?.email) {
                                    verifyIfUserExists(newSubscriber.email);
                                }
                            }}
                        />
                    </Grid>

                    <Grid xs={12}>
                        <EnhancedTextInput
                            id="first-name-input"
                            label="First Name"
                            ref={firstNameRef}
                            value={newSubscriber?.firstName || ""}
                            onChange={onFirstNameChange}
                            required={true}
                            fullWidth={true}
                            disabled={isLoading || doesUserExist == null || existingUser?.firstName}
                            validations={[requiredValidation]}
                        />
                    </Grid>

                    <Grid xs={12}>
                        <EnhancedTextInput
                            id="last-name-input"
                            label="Last Name"
                            ref={lastNameRef}
                            value={newSubscriber?.lastName || ""}
                            onChange={onLastNameChange}
                            required={true}
                            fullWidth={true}
                            disabled={isLoading || doesUserExist == null || existingUser?.lastName}
                            validations={[requiredValidation]}
                        />
                    </Grid>

                    <Grid xs={12}>
                        <EnhancedTextInput
                            id="phone-number-input"
                            label="Phone Number"
                            value={newSubscriber?.phoneNumber || ""}
                            onChange={onPhoneNumberChange}
                            fullWidth={true}
                            disabled={doesUserExist == null || isLoading}
                        />
                    </Grid>

                    <Grid xs={12} style={{ margin: "10px 0px 0px 3px" }}>
                        <Typography fontWeight={700}>Subscription Type *</Typography>

                        <Checkbox
                            label="Email"
                            id="email-subscription-checkbox"
                            data-testid="email-subscription-checkbox"
                            checked={emailSubscription}
                            disabled={doesUserExist == null || isLoading}
                            onChange={(e) => {
                                setEmailSubscription(e.target.checked);
                                setIsDirty(true);
                            }}
                        />

                        <Checkbox
                            label="SMS"
                            id="sms-subscription-checkbox"
                            data-testid="sms-subscription-checkbox"
                            checked={smsSubscription}
                            disabled={doesUserExist == null || isLoading}
                            onChange={(e) => {
                                setSmsSubscription(e.target.checked);
                                setIsDirty(true);
                            }}
                        />
                    </Grid>
                </Grid>
            </Dialog>

            <SimpleDialog
                title="Changes unsaved"
                primaryBtnText="Continue"
                isOpen={isDirty && showCustomDirtyDialog}
                onClose={() => {
                    setShowCustomDirtyDialog(false);
                }}
                primaryAction={() => {
                    setIsDirty(false);
                    setShowCustomDirtyDialog(false);
                    onClose();
                    setNewSubscriber(null);
                }}
            >
                All changes unsaved will be lost. Are you sure you want to continue?
            </SimpleDialog>
        </>
    );
};

export default AddSubscriberDialog;
